import { useContext, useEffect, useMemo, useState } from 'react';
import { ReactReduxContext } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Button, IconButton, Paper, Skeleton, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import dayjs from 'dayjs';
import WarningPage from './WarningPage';
import useWarningMonth from './useWarningMonth';
import WarningLoading from './WarningLoading';
import RetryNotice, { RetryInline } from './RetryNotice';
import { oldestFetchedAt } from './dashboardCache';
import { useDashboardRequest } from './useDashboardRequest';
import { isFinance } from './financeAccess';
import { approvalTarget, readViewer } from './approvalRoutes';
import { isCro, CRO_WORKFLOW_PATH } from './croAccess';
import { isOgdViewer } from './viewerDepartment';
import { NO_PERFORMANCE_METRICS_TITLE, NO_PERFORMANCE_METRICS_HELP } from './PerformanceMetricsGuard';
import { cardsForView, hasShortList, lensFrom } from './metricViews';
import useMyView, { canSeeCroWorkflow } from './useMyView';
import useConfigAccess from './useConfigAccess';
import { PROJECT_DUES_URL } from './projectDues';

import { WD, FONT_SERIF } from './warningTheme';

const GRID_SX = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)', xl: 'repeat(5, 1fr)' },
  gap: 1.5,
};

// "Lead Conversion Rate (LCR)" / "ACL - Average Customer Lifespan" -> { code, title } so the short code can
// sit in a chip and the readable name stays the card title. Labels with no code ("Refunds") get no chip.
const splitLabel = (label) => {
  let m = label.match(/^(.*?)\s*\(([A-Z]{2,6})\)\s*$/);
  if (m) return { code: m[2], title: m[1] };
  m = label.match(/^([A-Z]{2,6})\s+-\s+(.*)$/);
  if (m) return { code: m[1], title: m[2] };
  return { code: null, title: label };
};

// Tone-on-tone card palettes - the whole card takes the status colour as a soft tint and every word on it is a deep
// shade of that same hue (the tinted stat tiles of Stripe's and Apple Health's dashboards, Notion's callouts). One hue
// per card, so a row of cards reads as a row of states before a single word is read. Contrast on every pair stays
// above 4.5:1 (deep text on a ~95% tint).
//   bg      the tile fill            border   a slightly deeper step of the same tint
//   text    titles and the status    muted    subtitles, eyebrow, the gear icon
//   accent  the status dot, the "needs attention" ring and the hover glow
// Card palettes: a deep-to-mid gradient of the status colour with white text on top. Both ends of every gradient keep
// white above 4.5:1 (the lighter end is the limit: 5.5:1 or better), and the dimmed subtitle (white at 88%) stays above
// 4.5:1 as well. `from` doubles as the pill text / dot colour on the white status pill.
const TONES = {
  // warning: a light orange gradient, so it reads as "look here" without the alarm of red. Light fills cannot carry
  // white text, so this tone brings its own dark ink (8:1 on the light end, 5.9:1 on the deep end; dim ink 4.5:1+).
  warning: { from: '#FFB454', to: '#F98A2B', text: '#4A1F00', dim: 'rgba(74,31,0,0.9)', pill: '#4A1F00' },
  clear: { from: '#0C5232', to: '#17784B' },
  info: { from: '#35256E', to: '#5D43AE' },
  neutral: { from: '#474741', to: '#66665F' },
};
const WHITE = '#FFFFFF';
const WHITE_DIM = 'rgba(255,255,255,0.88)';

// The one card used everywhere on this page (metrics, workflow stages, approvals, project dues): a gradient tile in the
// status colour, no border, white text. Top row: a white status pill with a coloured dot (Linear's status chips) and the
// gear. Then the metric code as a small eyebrow, the title, the subtitle. The card's number sits as a large faded serif
// numeral in the bottom-right corner (editorial / magazine style) so it is there without taking a slot in the text. The
// tone itself is the "needs attention" signal (red tile = look here) - no extra ring or border - so `highlight` is
// accepted for the callers' sake but draws nothing.
//   chip: { tone: 'warning' | 'clear' | 'info' | 'neutral', text }
const PlaybookCard = ({ index, code, title, subtitle, chip, highlight, loading, error, onRetry, hidden, onClick, onConfigure }) => {
  const toneKey = error ? 'warning' : loading || !chip ? 'neutral' : chip.tone;
  const t = TONES[toneKey] || TONES.neutral;
  const ink = t.text || WHITE; // text colour on this tile: white on the deep gradients, dark on the light orange one
  const dim = t.dim || WHITE_DIM;
  const pillInk = t.pill || t.from;
  return (
    <Paper
      onClick={onClick}
      role="button"
      tabIndex={0}
      aria-label={`${title}${chip ? ` - ${chip.text}` : ''}`}
      onKeyDown={(e) => {
        if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onClick?.();
        }
      }}
      elevation={0}
      sx={{
        display: hidden ? 'none' : 'block',
        position: 'relative',
        overflow: 'hidden',
        outlineOffset: 3,
        '&:focus-visible': { outline: `2px solid ${t.to}` },
        p: 2,
        borderRadius: '20px',
        cursor: 'pointer',
        bgcolor: t.from,
        backgroundImage: `linear-gradient(135deg, ${t.from} 0%, ${t.to} 100%)`,
        color: ink,
        border: 'none',
        boxShadow: 'none',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        // a soft white glow in the top-right corner so the gradient has a light source
        '&::after': { content: '""', position: 'absolute', top: -60, right: -50, width: 160, height: 160, borderRadius: '50%', bgcolor: '#fff', opacity: 0.1, pointerEvents: 'none' },
        '&:hover': { transform: 'translateY(-3px)', boxShadow: `0 14px 28px ${t.to}66` },
        '@media (prefers-reduced-motion: reduce)': { transition: 'none', '&:hover': { transform: 'none' } },
      }}
    >
      {/* big faded number, bottom-right */}
      <Typography
        aria-hidden
        sx={{ position: 'absolute', right: 14, bottom: -4, fontFamily: FONT_SERIF, fontSize: 52, fontWeight: 600, lineHeight: 1, color: ink, opacity: 0.18, pointerEvents: 'none', userSelect: 'none' }}
      >
        {index}
      </Typography>

      <Box display="flex" alignItems="center" gap={0.75} flexWrap="wrap" sx={{ minHeight: 28, position: 'relative' }}>
        {loading ? (
          <Skeleton variant="rounded" width={84} height={24} sx={{ bgcolor: 'rgba(255,255,255,0.3)', borderRadius: '999px' }} />
        ) : (
          chip && (
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, bgcolor: WHITE, color: pillInk, borderRadius: '999px', pl: 0.9, pr: 1.2, py: '4px', fontSize: 12, fontWeight: 700, lineHeight: 1.4, whiteSpace: 'nowrap', boxShadow: '0 1px 2px rgba(0,0,0,0.15)' }}>
              <Box component="span" aria-hidden sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: t.to, flexShrink: 0 }} />
              <span>{chip.text}</span>
            </Box>
          )
        )}
        {onConfigure && (
          <Tooltip title="Configure threshold">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onConfigure();
              }}
              sx={{ ml: 'auto', mr: -0.5, color: ink, opacity: 0.8, '&:hover': { opacity: 1, bgcolor: 'rgba(255,255,255,0.25)' } }}
            >
              <TuneRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {code && (
        <Typography component="span" sx={{ display: 'block', fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', color: dim, textTransform: 'uppercase', lineHeight: 1, mt: 1.5, position: 'relative' }}>
          {code}
        </Typography>
      )}
      <Typography sx={{ fontSize: 16, fontWeight: 700, color: ink, mt: code ? 0.5 : 1.5, lineHeight: 1.3, position: 'relative', pr: 4 }}>{title}</Typography>
      <Typography component="div" sx={{ fontSize: 13, color: dim, mt: 0.5, position: 'relative', pr: 4, '& .MuiButton-root': { color: ink, textDecoration: 'underline' } }}>
        {error ? <RetryInline message={error} onRetry={onRetry} /> : subtitle}
      </Typography>
    </Paper>
  );
};

// Section heading: a thick green rule on the left, serif title, optional one-line description, actions on the right.
const SectionHead = ({ id, title, desc, action }) => (
  <Box id={id} display="flex" alignItems="flex-end" justifyContent="space-between" flexWrap="wrap" gap={2} sx={{ mb: 2.5, scrollMarginTop: 80 }}>
    <Box display="flex" alignItems="stretch" gap={1.5}>
      <Box aria-hidden sx={{ width: 6, borderRadius: 3, bgcolor: WD.green, flexShrink: 0 }} />
      <Box>
        <Typography component="h2" sx={{ fontFamily: FONT_SERIF, fontSize: 24, fontWeight: 600, color: WD.ink, lineHeight: 1.15 }}>{title}</Typography>
        {desc && <Typography sx={{ fontSize: 13, color: WD.inkMuted, mt: 0.5 }}>{desc}</Typography>}
      </Box>
    </Box>
    {action}
  </Box>
);

// One card per metric, reusing whatever summary numbers that metric's own
// dashboard endpoint already returns (totalEvaluated/warningCount) rather
// than a second summary-only endpoint - the row-level detail is wasted
// work for a card, but at today's data volume that's a fine trade against
// building and maintaining a second endpoint for one metric.
//
// Metrics are hardcoded here on purpose, not driven by a registry - adding
// ACR alongside LCR took ~10 lines, same as the first one. Revisit this at
// the next metric (LTA) if it's still this cheap to just add another card.
const MetricCard = ({ index, label, endpoint, month, refreshKey = 0, onClick, onConfigure, onResult, hidden }) => {
  const [reloadKey, setReloadKey] = useState(0); // Retry on a failed card re-runs just this request
  const url = `${endpoint}?month=${month.format('YYYY-MM-DD')}`;
  // The answer is remembered (dashboardCache.js): coming back to this page shows it at once. Only a SUCCESSFUL answer
  // counts as data, so a server error is an error - never "0 evaluated - All clear" (one broken metric used to be able
  // to make the whole page say everything is fine).
  const { data, error, loading } = useDashboardRequest(url, reloadKey + refreshKey);
  const summary = data ? { totalEvaluated: data.totalEvaluated || 0, warningCount: data.warningCount || 0 } : null;

  // Tell the page what this card found - and tell it again whenever the month changes, because the answer for the new
  // month may come straight from memory with no load in between.
  useEffect(() => {
    if (summary) onResult?.(summary.warningCount);
    else if (error) onResult?.(null); // reported as "could not load", not as 0 warnings
    // onResult is a new function every render of the page; re-reporting on that would loop - what matters is the month
    // (url), the count and the error
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, summary?.warningCount, error]);

  const hasWarnings = (summary?.warningCount || 0) > 0;
  const { code, title } = splitLabel(label);
  const chip = summary && { tone: hasWarnings ? 'warning' : 'clear', text: hasWarnings ? `${summary.warningCount} ${summary.warningCount === 1 ? 'warning' : 'warnings'}` : 'All clear' };

  return (
    <PlaybookCard
      index={index}
      code={code}
      title={title}
      subtitle={summary ? `${summary.totalEvaluated} evaluated · ${month.format('MMM YYYY')}` : 'Calculating…'}
      chip={chip}
      onRetry={() => setReloadKey((k) => k + 1)}
      highlight={hasWarnings}
      loading={loading}
      error={error}
      hidden={hidden}
      onClick={onClick}
      onConfigure={onConfigure}
    />
  );
};

// A plain count card, no red/green threshold coloring - for the "No
// Threshold" metrics (Fee Transfer Count), which
// have no warning concept - and no config screen - at all, unlike
// MetricCard's metrics. Uses the "info" (blue) tone instead of a status
// color, since there's no status to report, just a fact.
const InfoCard = ({ index, label, endpoint, month, valueField, refreshKey = 0, onClick, hidden }) => {
  const [reloadKey, setReloadKey] = useState(0);
  const url = `${endpoint}?month=${month.format('YYYY-MM-DD')}`;
  const { data, error, loading } = useDashboardRequest(url, reloadKey + refreshKey);
  const d = data || {};
  const value = data ? d[valueField] ?? 0 : null;
  // Fee Transfer: the big number is the total, so say how many PEOPLE it is spread over (and the rupees) - the card used to show the people count as if it were the total.
  const parts = [];
  if (valueField === 'totalCount' && d.totalEvaluated != null) parts.push(`${d.totalEvaluated} ${d.totalEvaluated === 1 ? 'person' : 'people'}`);
  if (d.totalValue != null) parts.push(`₹${Number(d.totalValue).toLocaleString('en-IN')}`);
  const detail = data ? parts.join(' · ') : '';

  const { code, title } = splitLabel(label);
  return (
    <PlaybookCard
      index={index}
      code={code}
      title={title}
      subtitle={loading ? 'Calculating…' : `${detail || 'Count only'} · ${month.format('MMM YYYY')}`}
      chip={value !== null && { tone: 'info', text: String(value) }}
      onRetry={() => setReloadKey((k) => k + 1)}
      loading={loading}
      error={error}
      hidden={hidden}
      onClick={onClick}
    />
  );
};

// configPath points each card straight at its OWN threshold screen -
// several metrics deliberately share one config screen (see each
// controller's own header comment for why), so several rows below point
// at the same path on purpose, not by mistake.
const METRIC_CARDS = [
  { label: 'Lead Conversion Rate (LCR)', endpoint: 'warning-dashboard/lcr-dashboard', path: 'lcr', configPath: 'lcr-config' },
  { label: 'Assessment Conversion Rate (ACR)', endpoint: 'warning-dashboard/acr-dashboard', path: 'acr', configPath: 'acr-config' },
  { label: 'Lead to Admission Rate (LTA)', endpoint: 'warning-dashboard/lta-dashboard', path: 'lta', configPath: 'lta-config' },
  { label: 'Slot Fulfillment Rate (SFR)', endpoint: 'warning-dashboard/sfr-dashboard', path: 'sfr', configPath: 'sfr-config' },
  { label: 'Target Achievement Rate (TAR)', endpoint: 'warning-dashboard/tar-dashboard', path: 'tar', configPath: 'target-slab-config' },
  { label: 'Average Revenue Per User (ARPU)', endpoint: 'warning-dashboard/arpu-dashboard', path: 'arpu', configPath: 'target-slab-config' },
  { label: 'Number of Admissions', endpoint: 'warning-dashboard/admission-count-dashboard', path: 'admission-count', configPath: 'target-slab-config' },
  { label: 'New Lead Assignment Count', endpoint: 'warning-dashboard/new-lead-assignment-count-dashboard', path: 'new-lead-assignment-count', configPath: 'new-lead-assignment-count-config' },
  { label: 'Fee Collection', endpoint: 'warning-dashboard/fee-collection-dashboard', path: 'fee-collection', configPath: 'fee-collection-config' },
  { label: 'Weekly Revenue', endpoint: 'warning-dashboard/weekly-revenue-dashboard', path: 'weekly-revenue', configPath: 'weekly-revenue-config' },
  { label: 'MAU - Monthly Active Students', endpoint: 'warning-dashboard/mau-dashboard', path: 'mau', configPath: 'mau-config' },
  { label: 'Sales Count', endpoint: 'warning-dashboard/sales-count-dashboard', path: 'sales-count', configPath: 'crm-team-config' },
  { label: 'ACL - Average Customer Lifespan', endpoint: 'warning-dashboard/acl-dashboard', path: 'acl', configPath: 'project-threshold-config' },
  { label: 'CCR - Course Completion Ratio', endpoint: 'warning-dashboard/ccr-dashboard', path: 'ccr', configPath: 'project-threshold-config' },
  { label: 'FDR - Fee Default Rate', endpoint: 'warning-dashboard/fdr-dashboard', path: 'fdr', configPath: 'project-threshold-config' },
  { label: 'SAR - Session Achievement Rate', endpoint: 'warning-dashboard/sar-dashboard', path: 'sar', configPath: 'project-threshold-config' },
  { label: 'ATP - Average Tutor Payment', endpoint: 'warning-dashboard/atp-dashboard', path: 'atp', configPath: 'project-threshold-config' },
  { label: 'Sales Cycle Length', endpoint: 'warning-dashboard/sales-cycle-length-dashboard', path: 'sales-cycle-length', configPath: 'sales-cycle-length-config' },
  { label: 'ATS - Average Time to Start', endpoint: 'warning-dashboard/ats-dashboard', path: 'ats', configPath: 'ats-config' },
  { label: 'Revenue Per OGA', endpoint: 'warning-dashboard/revenue-per-oga-dashboard', path: 'revenue-per-oga', configPath: 'ogd-team-config' },
  { label: 'LCRR - Lead Cost Revenue Ratio', endpoint: 'warning-dashboard/lcrr-dashboard', path: 'lcrr', configPath: 'target-slab-config' },
  { label: 'Refunds', endpoint: 'warning-dashboard/refunds-dashboard', path: 'refunds', configPath: 'refunds-config' },
  { label: 'Tutor Count', endpoint: 'warning-dashboard/tutor-count-dashboard', path: 'tutor-count', configPath: 'tutor-count-config' },
  { label: 'Tutor Replacement Count', endpoint: 'warning-dashboard/tutor-replacement-count-dashboard', path: 'tutor-replacement-count', configPath: 'tutor-replacement-count-config' },
  { label: 'Tutor Replacement Count (by Tutor)', endpoint: 'warning-dashboard/tutor-replacement-by-tutor-dashboard', path: 'tutor-replacement-by-tutor', configPath: 'tutor-replacement-by-tutor-config' },
];
// CRO Pending Tasks (new metric, sourced from the retention workflow) gets its own section below
// (CroWorkflowSection), not a flat card here - it's inherently per-stage, not a single number.

// No Threshold model - nothing to configure, so no configPath and no gear
// icon on these three.
const INFO_CARDS = [
  { label: 'Fee Transfer Count', endpoint: 'warning-dashboard/fee-transfer-count-dashboard', path: 'fee-transfer-count', valueField: 'totalCount' },
];

const PHASE_LABEL = { 1: 'Onboarding', 2: 'Demo & Admission', 3: 'Tracking & Retention' };

// One small card per workflow stage - stageNo/shortName, pending count, and an overdue pill when the stage
// has anything past its configured threshold. Reuses PlaybookCard so it reads as part of the same page,
// just a tighter card than MetricCard since 13 of these sit together.
const StageCard = ({ stage, onClick }) => {
  return (
    <PlaybookCard
      index={stage.stageNo}
      title={stage.shortName}
      subtitle={`${stage.pendingCount} pending · threshold ${stage.thresholdHours}h`}
      chip={{ tone: stage.isWarning ? 'warning' : 'clear', text: stage.isWarning ? `${stage.value} overdue` : 'On time' }}
      highlight={stage.isWarning}
      onClick={() => onClick(stage.stageNo)}
    />
  );
};

// CRO Workflow - its own section, separate from the Requirement Document metric grid above. One request to
// cro-pending-tasks-dashboard gives all 13 stages at once, so this renders straight from that instead of 13
// separate card fetches. No month picker here either (see CroPendingTasksDashboard.jsx) - always live.
const CroWorkflowSection = ({ onOpenStage, onConfigure, onOpenWorkflow, refreshKey = 0 }) => {
  const [reloadKey, setReloadKey] = useState(0);
  // freshness 10s: it is live work (leads waiting on a CRO), so a short stay away still refreshes it quietly
  const { data, error, loading } = useDashboardRequest('warning-dashboard/cro-pending-tasks-dashboard', reloadKey + refreshKey, { freshMs: 10 * 1000 });
  const rows = data?.rows || [];

  const byPhase = new Map();
  for (const r of rows) {
    if (!byPhase.has(r.phaseNo)) byPhase.set(r.phaseNo, []);
    byPhase.get(r.phaseNo).push(r);
  }

  return (
    <Box sx={{ mt: 4 }}>
      <SectionHead
        id="cro-workflow"
        title="CRO Workflow"
        desc={data?.enrolledFrom ? `Enrolments from ${dayjs(data.enrolledFrom).format('D MMM YYYY')} onwards` : undefined}
        action={(onOpenWorkflow || onConfigure) && (
          <Box display="flex" alignItems="center" gap={1}>
            {onOpenWorkflow && (
              <Button variant="outlined" endIcon={<ArrowForwardRoundedIcon />} onClick={onOpenWorkflow} sx={{ color: WD.green, borderColor: WD.green }}>
                Complete tasks in CRO workflow
              </Button>
            )}
            {onConfigure && (
              <Tooltip title="Configure per-stage thresholds">
                <IconButton size="small" onClick={onConfigure} sx={{ color: WD.inkFaint, '&:hover': { color: WD.inkMuted, bgcolor: '#EFEDE6' } }}>
                  <TuneRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        )}
      />

      {loading && <WarningLoading message="Loading workflow stages…" />}
      {!loading && error && <RetryNotice message={error} onRetry={() => setReloadKey((k) => k + 1)} />}

      {!loading && !error && [1, 2, 3].map((phaseNo) => {
        const stages = (byPhase.get(phaseNo) || []).slice().sort((a, b) => a.stageNo - b.stageNo);
        if (stages.length === 0) return null;
        return (
          <Box key={phaseNo} sx={{ mb: 2.5 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: WD.green, mb: 1, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              {PHASE_LABEL[phaseNo] || `Phase ${phaseNo}`}
            </Typography>
            <Box sx={GRID_SX}>
              {stages.map((stage) => (
                <StageCard key={stage.stageNo} stage={stage} onClick={onOpenStage} />
              ))}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

// One small card per approval type - count, red/green by whether there's a backlog. Same visual language as
// StageCard, just one flat row instead of phase groups (only 4 types, not worth grouping).
const ApprovalCard = ({ index, row, onClick }) => {
  return (
    <PlaybookCard
      index={index}
      title={row.label}
      subtitle="Waiting on approval"
      chip={{ tone: row.isWarning ? 'warning' : 'clear', text: `${row.count} pending` }}
      highlight={row.isWarning}
      onClick={() => onClick(row.key, row)}
    />
  );
};

// Where each card opens - see approvalRoutes.js (the app's own approval screen where one exists, the department leave
// approvals page for the Leave cards, this dashboard's read-only table when the viewer cannot open the real screen).

// Pending Approvals - leave (RH + HR), WFH grant requests (RH + HR), attendance regularization, compensatory off. Own section, separate
// from both the Requirement Document metric grid and CRO Workflow - different domain (HR/attendance, not
// sales/retention), different shape (plain counts, no threshold/config). One request gives every approval card.
const PendingApprovalsSection = ({ onOpenType, refreshKey = 0 }) => {
  const [reloadKey, setReloadKey] = useState(0);
  // freshness 10s: approvals change as people act on them, so a short stay away still refreshes it quietly
  const { data, error, loading } = useDashboardRequest('warning-dashboard/pending-approvals-dashboard', reloadKey + refreshKey, { freshMs: 10 * 1000 });
  const rows = data?.rows || [];

  // No approval cards for this person (RH cards are for reporting heads, HR cards for HR - most people have none) = no
  // heading and no space. Nothing while the first answer is still loading either, so the heading never shows up and then
  // disappears. A failed load keeps the heading with its Retry, so a real problem is never hidden.
  if (loading) return null;
  if (!error && rows.length === 0) return null;

  return (
    <Box sx={{ mb: 4 }}>
      <SectionHead
        id="pending-approvals"
        title="Pending Approvals"
      />

      {error && <RetryNotice message={error} onRetry={() => setReloadKey((k) => k + 1)} />}

      {!error && (
        <Box sx={GRID_SX}>
          {rows.map((row, i) => (
            <ApprovalCard key={row.key} index={i + 1} row={row} onClick={onOpenType} />
          ))}
        </Box>
      )}
    </Box>
  );
};

// Project Management - what is late, and what is about to be, in InteTasks (the project tool): overdue tasks, tasks due in the next
// 7 days, overdue projects and date-change requests nobody has decided. Own section (a different system from the performance metrics and
// the CRO workflow), numbers only - no threshold, no warning, and it is NOT part of the "warnings across N metrics" headline above.
// Always live (InteTasks keeps no history), so no month: one request gives every card, and a card opens the Project Dues page.
const ProjectManagementSection = ({ onOpen, refreshKey = 0 }) => {
  const [reloadKey, setReloadKey] = useState(0);
  const { data: answer, error, loading } = useDashboardRequest(PROJECT_DUES_URL, reloadKey + refreshKey, { freshMs: 10 * 1000 });
  // An answer that is not the shape this section reads (a server that is older or newer than this page) is treated as "could not load"
  // - it must never be able to take the whole landing page down with it.
  const data = answer && answer.totals && Array.isArray(answer.projects) ? answer : null;
  const unreadable = !!answer && !data;
  const t = data?.totals;
  const late = (n) => (n > 0 ? 'warning' : 'clear');
  const cards = t ? [
    { key: 'overdue', title: 'Overdue tasks', subtitle: 'Open and past the due date', n: t.overdueTasks, tone: late(t.overdueTasks), text: t.overdueTasks > 0 ? `${t.overdueTasks} overdue` : 'None overdue' },
    { key: 'soon', title: 'Due in 7 days', subtitle: `${t.dueToday} due today`, n: t.dueSoonTasks, tone: 'info', text: `${t.dueSoonTasks} due soon` },
    { key: 'projects', title: 'Overdue projects', subtitle: `of ${t.openProjects} open ${t.openProjects === 1 ? 'project' : 'projects'}`, n: t.overdueProjects, tone: late(t.overdueProjects), text: t.overdueProjects > 0 ? `${t.overdueProjects} overdue` : 'None overdue' },
    { key: 'requests', title: 'Date requests waiting', subtitle: 'Waiting for a decision', n: t.pendingDateRequests, tone: 'info', text: `${t.pendingDateRequests} waiting` },
  ] : [];

  return (
    <Box sx={{ mt: 4 }}>
      <SectionHead
        id="project-management"
        title="Project Management"
        desc="InteTasks · your work and your team's · as of today"
        action={(
          <Button variant="outlined" endIcon={<ArrowForwardRoundedIcon />} onClick={onOpen} sx={{ color: WD.green, borderColor: WD.green }}>
            Project dues in detail
          </Button>
        )}
      />

      {loading && !answer && <WarningLoading message="Loading project dues…" />}
      {!loading && !data && (error || unreadable) && <RetryNotice message={error || 'Project dues came back in a form this page cannot read'} onRetry={() => setReloadKey((k) => k + 1)} />}

      {data && (
        <>
          <Box sx={GRID_SX}>
            {cards.map((c, i) => (
              <PlaybookCard key={c.key} index={i + 1} title={c.title} subtitle={c.subtitle} chip={{ tone: c.tone, text: c.text }} onClick={onOpen} />
            ))}
          </Box>
          {t.totalTasks === 0 && data.projects.length === 0 && (
            <Typography sx={{ fontSize: 13, color: WD.inkMuted, mt: 1.5 }}>No InteTasks work is assigned to you or your team yet.</Typography>
          )}
        </>
      )}
    </Box>
  );
};

// Landing page for the Warning Dashboard - meant to grow into one card per
// metric as each gets built.
const scrollToSection = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

const WarningDashboardHome = () => {
  const navigate = useNavigate();
  // the viewer's nav permissions live in the app's store; without one (tests, storybook) there are none
  const reduxContext = useContext(ReactReduxContext);
  const [searchParams, setSearchParams] = useSearchParams();
  // The month and the "needs attention" filter live in the URL, so the browser's Back button (and a link back from a
  // detail page) returns to exactly the screen that was left - not to the current month with the filter reset.
  // The month is shared with every other Warning Dashboard screen and remembered for the session, so coming back
  // from a results or threshold screen lands on the month that was being worked on (useWarningMonth.js).
  const [month, setMonth] = useWarningMonth();
  const onlyAttention = searchParams.get('attention') === '1';
  const changeParams = (change) => {
    const next = new URLSearchParams(searchParams);
    change(next);
    setSearchParams(next, { replace: true }); // replace: stepping through months must not fill the Back history
  };
  const setOnlyAttention = (on) => changeParams((p) => (on ? p.set('attention', '1') : p.delete('attention')));
  // Which cards: the front-line teams (OGD people and project-department CROs with nobody under them) start with a short list; the
  // "Advanced" switch shows everything. It lives in the URL like the attention filter (so Back from a card keeps it) but is NOT
  // remembered for the next visit - every visit starts with the short list.
  const { view, performanceMetrics, croWorkflow } = useMyView();
  // what this person may configure: Finance everything; others only what Finance granted (see useConfigAccess)
  const { level: configLevel, canConfigure } = useConfigAccess();
  const advanced = searchParams.get('advanced') === '1';
  const setAdvanced = (on) => changeParams((p) => (on ? p.set('advanced', '1') : p.delete('advanced')));
  const shortList = hasShortList(view) && !advanced;
  // People who see every card by default (Finance, managers, other departments) can narrow the page to the OGD set or the CRO set
  // (the same two lists). Same idea as Advanced: in the URL (?metrics=ogd|cro), not remembered for the next visit.
  const lens = view === 'all' ? lensFrom(searchParams.get('metrics')) : 'all';
  const setLens = (next) => changeParams((p) => (next === 'all' ? p.delete('metrics') : p.set('metrics', next)));
  const cards = useMemo(
    () => cardsForView(shortList ? view : lens, [...METRIC_CARDS.map((c) => ({ ...c, kind: 'metric' })), ...INFO_CARDS.map((c) => ({ ...c, kind: 'info' }))]),
    [shortList, view, lens],
  );
  const metricCards = cards.filter((c) => c.kind === 'metric');
  const [refreshKey, setRefreshKey] = useState(0); // the Refresh button: every card asks the server again

  // What each metric card found, kept per month. A card's answer can arrive instantly from memory, and a child's
  // effect runs before its parent's - so "clear the results when the month changes" would wipe what the cards had
  // just reported; keying by month needs no clearing at all.
  const monthKey = month.format('YYYY-MM');
  const [allResults, setAllResults] = useState({});
  const results = useMemo(
    () => Object.fromEntries(Object.entries(allResults).filter(([k]) => k.startsWith(`${monthKey}|`)).map(([k, v]) => [k.slice(monthKey.length + 1), v])),
    [allResults, monthKey],
  );
  const reportResult = (path) => (count) => setAllResults((r) => (r[`${monthKey}|${path}`] === count ? r : { ...r, [`${monthKey}|${path}`]: count }));
  // only the cards on screen count (a card hidden by the short list is not fetched, and must not colour the headline)
  const shownPaths = new Set(metricCards.map((m) => m.path));
  const shownResults = Object.entries(results).filter(([path]) => shownPaths.has(path)).map(([, count]) => count);
  const loadedCount = shownResults.length;
  const failedMetrics = shownResults.filter((c) => c === null).length; // reported as null = could not load
  const flaggedMetrics = shownResults.filter((c) => c > 0).length;
  const totalWarnings = shownResults.reduce((a, b) => a + (b || 0), 0);
  const attention = flaggedMetrics > 0 || failedMetrics > 0;
  const failedText = failedMetrics ? `${failedMetrics} ${failedMetrics === 1 ? 'metric' : 'metrics'} could not load` : '';
  const headline = flaggedMetrics
    ? `${totalWarnings} ${totalWarnings === 1 ? 'warning' : 'warnings'} across ${flaggedMetrics} ${flaggedMetrics === 1 ? 'metric' : 'metrics'}${failedText ? ` · ${failedText}` : ''}`
    : failedText || 'All metrics clear';
  const allLoaded = view !== null && loadedCount >= metricCards.length;
  // when the numbers on screen were last fetched (only known when answers are remembered, see dashboardCache.js)
  const cardUrls = useMemo(() => cards.map((m) => `${m.endpoint}?month=${month.format('YYYY-MM-DD')}`), [cards, month]);
  const updatedAt = allLoaded ? oldestFetchedAt(cardUrls) : null;

  const goTo = (path) => navigate(`/dashboard/warning-dashboard/${path}?month=${month.format('YYYY-MM-DD')}`);
  // the CRO Workflow section (and the jump button to it): Finance decides per department ("Who can see CRO Workflow" on Manage access). It
  // waits for the one my-view answer, so a person whose department is switched off never sends the request the server would refuse. With no
  // answer (an older server, a failed request) it is what it always was: left out for OGD staff, who are never a student's CRO.
  const showCroWorkflow = view !== null && canSeeCroWorkflow(croWorkflow, isOgdViewer());
  // Finance can switch Performance Metrics off for a department ("Who can see Performance Metrics"). Then this person gets no block, no
  // cards and no card requests - just one plain sentence. Only an explicit "no" hides it (see useMyView).
  const showPerformanceMetrics = performanceMetrics !== false;

  return (
    <WarningPage title="Warning Dashboard">
      <Box>
        <PendingApprovalsSection
          onOpenType={(key, row) => navigate(approvalTarget(key, { ...readViewer(), navChild: reduxContext?.store?.getState()?.role?.navChild || [] }, row))}
          refreshKey={refreshKey}
        />

        {showPerformanceMetrics ? (
        <Box>
        {/* Hero panel: the section heading, the one-line verdict for the month as a big serif headline, the month / refresh
            controls and the view toggles - all on one dark green canvas (the "command bar" of Linear / Vercel dashboards),
            so the cards below sit on plain white. */}
        <Box
          id="performance-metrics"
          sx={{
            position: 'relative',
            overflow: 'hidden',
            bgcolor: WD.green,
            color: '#fff',
            borderRadius: '24px',
            p: { xs: 2.5, md: 3.5 },
            mb: 3,
            scrollMarginTop: 80,
            '&::before': { content: '""', position: 'absolute', right: -120, top: -140, width: 360, height: 360, borderRadius: '50%', bgcolor: '#fff', opacity: 0.06, pointerEvents: 'none' },
            '&::after': { content: '""', position: 'absolute', right: 120, bottom: -200, width: 300, height: 300, borderRadius: '50%', bgcolor: '#fff', opacity: 0.05, pointerEvents: 'none' },
          }}
        >
          <Box display="flex" alignItems="flex-start" justifyContent="space-between" flexWrap="wrap" gap={2} sx={{ position: 'relative' }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography component="h2" sx={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.72)' }}>
                Performance Metrics
              </Typography>
              <Box display="flex" alignItems="center" gap={1.25} sx={{ mt: 1.25 }}>
                <Box aria-hidden sx={{ width: 12, height: 12, borderRadius: '50%', flexShrink: 0, bgcolor: !allLoaded ? 'rgba(255,255,255,0.5)' : attention ? '#FFB74D' : '#7FE0AE', boxShadow: `0 0 0 4px ${!allLoaded ? 'rgba(255,255,255,0.12)' : attention ? 'rgba(255,183,77,0.25)' : 'rgba(127,224,174,0.25)'}` }} />
                <Typography sx={{ fontFamily: FONT_SERIF, fontSize: { xs: 24, md: 30 }, fontWeight: 600, lineHeight: 1.15, color: '#fff' }}>
                  {!allLoaded ? (view === null ? 'Checking…' : `Checking ${loadedCount}/${metricCards.length}…`) : headline}
                </Typography>
              </Box>
              <Typography sx={{ fontSize: 13, color: 'rgba(255,255,255,0.75)', mt: 1 }}>
                {month.format('MMMM YYYY')} · Click a card for results
                {configLevel !== 'none' && <> , or the <TuneRoundedIcon sx={{ fontSize: 14, verticalAlign: 'text-bottom' }} /> icon to set its threshold</>}
              </Typography>
            </Box>

            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
              {showCroWorkflow && (
                <Button variant="outlined" endIcon={<ArrowForwardRoundedIcon />} onClick={() => scrollToSection('cro-workflow')} sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.55)', borderRadius: '999px', '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.12)' } }}>
                  CRO workflow
                </Button>
              )}
              {canConfigure('target-slab-config') && (
                <Button variant="outlined" endIcon={<ArrowForwardRoundedIcon />} onClick={() => goTo('target-slab-config')} sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.55)', borderRadius: '999px', '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.12)' } }}>
                  Configure thresholds
                </Button>
              )}
              {isFinance() && (
                <Button variant="outlined" endIcon={<ArrowForwardRoundedIcon />} onClick={() => navigate('/dashboard/warning-dashboard/access')} sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.55)', borderRadius: '999px', '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.12)' } }}>
                  Manage access
                </Button>
              )}
              {updatedAt && <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', ml: 0.5 }}>Updated {dayjs(updatedAt).format('HH:mm')}</Typography>}
              <Tooltip title="Refresh all numbers">
                <IconButton size="small" aria-label="Refresh all" onClick={() => setRefreshKey((k) => k + 1)} sx={{ color: 'rgba(255,255,255,0.8)', '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.15)' } }}>
                  <RefreshRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  views={['year', 'month']}
                  value={month}
                  onChange={(v) => v && setMonth(v)}
                  // no floating label: it sat on the white field and clashed with the value; the field is named for screen readers instead
                  slotProps={{ textField: { size: 'small', inputProps: { 'aria-label': 'Month' }, sx: { minWidth: 170, '& .MuiOutlinedInput-root': { bgcolor: '#fff', borderRadius: '999px' }, '& fieldset': { borderColor: 'transparent' } } } }}
                />
              </LocalizationProvider>
            </Box>
          </Box>

          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" sx={{ mt: 2.5, position: 'relative' }}>
            <ToggleButton
              value="attention"
              selected={onlyAttention}
              onChange={() => setOnlyAttention(!onlyAttention)}
              size="small"
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '999px', px: 1.75, py: 0.5, color: '#fff', borderColor: 'rgba(255,255,255,0.4)', '&:hover': { bgcolor: 'rgba(255,255,255,0.12)' }, '&.Mui-selected': { bgcolor: '#fff', color: WD.green, borderColor: '#fff', '&:hover': { bgcolor: '#fff' } } }}
            >
              Needs attention only
            </ToggleButton>
            {view === 'all' && (
              <ToggleButtonGroup
                exclusive
                size="small"
                value={lens}
                onChange={(e, next) => next && setLens(next)} // clicking the selected one keeps it (there is always one choice)
                aria-label="Which metrics to show"
                sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 600, px: 1.75, py: 0.5, color: '#fff', borderColor: 'rgba(255,255,255,0.4)', '&:hover': { bgcolor: 'rgba(255,255,255,0.12)' }, '&.Mui-selected': { bgcolor: '#fff', color: WD.green, borderColor: '#fff', '&:hover': { bgcolor: '#fff' } } }, '& .MuiToggleButton-root:first-of-type': { borderRadius: '999px 0 0 999px' }, '& .MuiToggleButton-root:last-of-type': { borderRadius: '0 999px 999px 0' } }}
              >
                <ToggleButton value="all" aria-label="All metrics">All metrics</ToggleButton>
                <ToggleButton value="ogd" aria-label="OGD metrics">OGD metrics</ToggleButton>
                <ToggleButton value="cro" aria-label="CRO metrics">CRO metrics</ToggleButton>
              </ToggleButtonGroup>
            )}
            {hasShortList(view) && (
              <Tooltip title={advanced ? 'Back to the metrics for your role' : `Showing the ${cards.length} metrics for your role - turn this on to see all of them`}>
                <ToggleButton
                  value="advanced"
                  selected={advanced}
                  onChange={() => setAdvanced(!advanced)}
                  size="small"
                  aria-label="Advanced: show all metrics"
                  sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '999px', px: 1.75, py: 0.5, color: '#fff', borderColor: 'rgba(255,255,255,0.4)', '&:hover': { bgcolor: 'rgba(255,255,255,0.12)' }, '&.Mui-selected': { bgcolor: '#fff', color: WD.green, borderColor: '#fff', '&:hover': { bgcolor: '#fff' } } }}
                >
                  Advanced: all metrics
                </ToggleButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        {view === null ? (
          <WarningLoading />
        ) : (
          <Box sx={GRID_SX}>
            {cards.map((m, i) => (m.kind === 'metric' ? (
              <MetricCard
                key={m.path}
                index={i + 1}
                label={m.label}
                endpoint={m.endpoint}
                month={month}
                refreshKey={refreshKey}
                hidden={onlyAttention && !(results[m.path] > 0 || results[m.path] === null)} // a card that could not load needs attention too
                onResult={reportResult(m.path)}
                onClick={() => goTo(m.path)}
                onConfigure={m.configPath && canConfigure(m.configPath) ? () => goTo(m.configPath) : undefined}
              />
            ) : (
              <InfoCard key={m.path} index={i + 1} label={m.label} endpoint={m.endpoint} month={month} refreshKey={refreshKey} valueField={m.valueField} hidden={onlyAttention} onClick={() => goTo(m.path)} />
            )))}
          </Box>
        )}
        {onlyAttention && allLoaded && flaggedMetrics === 0 && failedMetrics === 0 && (
          <Box sx={{ textAlign: 'center', py: 5, px: 2, bgcolor: TONES.clear.from, backgroundImage: `linear-gradient(135deg, ${TONES.clear.from} 0%, ${TONES.clear.to} 100%)`, color: WHITE, borderRadius: '20px' }}>
            <Typography sx={{ fontFamily: FONT_SERIF, fontSize: 20, fontWeight: 600 }}>Nothing needs attention</Typography>
            <Typography sx={{ fontSize: 13, color: WHITE_DIM, mt: 0.5 }}>Every metric is within its threshold for {month.format('MMMM YYYY')}.</Typography>
          </Box>
        )}
        </Box>
        ) : (
          <Box sx={{ textAlign: 'center', py: 5, px: 2, bgcolor: TONES.neutral.from, backgroundImage: `linear-gradient(135deg, ${TONES.neutral.from} 0%, ${TONES.neutral.to} 100%)`, color: WHITE, borderRadius: '20px' }}>
            <Typography sx={{ fontFamily: FONT_SERIF, fontSize: 20, fontWeight: 600 }}>{NO_PERFORMANCE_METRICS_TITLE}</Typography>
            <Typography sx={{ fontSize: 13, color: WHITE_DIM, mt: 0.5 }}>{NO_PERFORMANCE_METRICS_HELP}</Typography>
          </Box>
        )}

        {showCroWorkflow && (
          <CroWorkflowSection
            onOpenStage={(stageNo) => navigate(`/dashboard/warning-dashboard/cro-pending-tasks?stage=${stageNo}`)}
            onConfigure={canConfigure('cro-pending-tasks-config') ? () => navigate('/dashboard/warning-dashboard/cro-pending-tasks-config') : undefined}
            onOpenWorkflow={isCro() ? () => navigate(CRO_WORKFLOW_PATH) : undefined}
            refreshKey={refreshKey}
          />
        )}

        <ProjectManagementSection onOpen={() => navigate('/dashboard/warning-dashboard/project-dues')} refreshKey={refreshKey} />
      </Box>
    </WarningPage>
  );
};

export default WarningDashboardHome;
